import { useTableSortingAndPagination } from "#redux/reducers/tableStateReducer";
import { JobDrawer } from "#src/batteries-included-components/Drawers/JobDrawer";
import { UserDataDisplay } from "#src/components/Common/UserDataDisplay";
import { useListJobs } from "#src/components/hooks/adapters/useJobs";
import { useStickyState } from "#src/hooks/useStickyState";
import { useQueries, UseQueryOptions } from "@tanstack/react-query";
import {
  colours,
  DataTable,
  DataTablePanel,
  HeaderType,
  Icon,
  IconProps,
  Link,
  Pill,
  PillProps,
  StorageKeys,
  Tooltip,
} from "@validereinc/common-components";
import {
  JobAdapter,
  JobGetListFilterType,
  JobSchema,
  JobStatus,
  JobStatusType,
  JobType,
  JobTypes,
  JobTypeType,
  SortDirection,
  UsersAdapter,
  UserReturnType,
} from "@validereinc/domain";
import classNames from "classnames/bind";
import { isEmpty } from "es-toolkit/compat";
import React, { useCallback, useEffect, useState } from "react";
import styles from "./RunLogTablePanel.module.scss";

const cx = classNames.bind(styles);

export const RunLogTablePanel = ({
  viewConfigStorageKey,
  tableConfigStorageKey,
  tableStateStorageKey,
  onJobSelection: setSelectedJobCallback,
  defaultJobId,
}: Pick<
  StorageKeys,
  "viewConfigStorageKey" | "tableConfigStorageKey" | "tableStateStorageKey"
> & {
  onJobSelection?: (jobId?: string) => void;
  defaultJobId?: string;
}) => {
  const [filters] = useStickyState<JobGetListFilterType>(
    {},
    viewConfigStorageKey
  );
  const [tableState, updateTableState] = useTableSortingAndPagination(
    {
      sortBy: "started_at",
      sortDirection: SortDirection.DESCENDING,
    },
    undefined,
    tableStateStorageKey
  );
  const [selectedJob, setSelectedJobState] = useState<JobType | null>(null);

  const setSelectedJob: typeof setSelectedJobState = useCallback(
    (job) => {
      setSelectedJobCallback?.(job && "id" in job ? job.id : undefined);
      setSelectedJobState(job);
    },
    [setSelectedJobState]
  );

  const params: Parameters<typeof JobAdapter.getList>[0] = {
    page: tableState.page,
    pageSize: tableState.pageSize,
    sortBy: tableState.sortBy,
    sortDirection: tableState.sortDirection,
    filters: filters,
  };

  const { data, isLoading } = useListJobs(params);
  const items = data?.data ?? [];

  const runLogStartedByUsers = useQueries<
    Array<
      UseQueryOptions<
        Awaited<ReturnType<typeof UsersAdapter.getOne>> | undefined,
        unknown,
        UserReturnType | undefined
      >
    >
  >({
    queries:
      items.map((items) => ({
        queryKey: ["users", items.started_by],
        queryFn: () =>
          UsersAdapter.getOne({
            id: items.started_by ?? "",
          }),
        enabled: Boolean(items.started_by),
        staleTime: 3 * 60 * 1000,
        select: (resp) => resp?.data,
      })) ?? [],
  });

  const runLogStartedByUsersMap = React.useMemo<
    Record<string, UserReturnType>
  >(() => {
    return runLogStartedByUsers.reduce<Record<string, UserReturnType>>(
      (map: Record<string, UserReturnType>, q) => {
        if (!q.data?.id || map[q.data.id]) {
          return map;
        }

        map[q.data.id] = q.data;
        return map;
      },
      {}
    );
  }, [runLogStartedByUsers]);

  const getStatusDisplayLabel = (status?: JobStatusType) => {
    switch (status) {
      case JobStatus.STARTED:
        return "In-Progress";
      case JobStatus.FINISHED:
        return "Completed";
      case JobStatus.FAILED:
        return "Failed";
      default:
        return "Unknown";
    }
  };

  const getJobsStatusVariant = (
    status?: JobStatusType
  ): PillProps["variant"] => {
    switch (status) {
      case JobStatus.STARTED:
        return "info";
      case JobStatus.FINISHED:
        return "success";
      case JobStatus.FAILED:
        return "error";
      default:
        return "default";
    }
  };

  const getJobTypeDisplayLabel = (type?: JobTypeType) => {
    switch (type) {
      case JobTypes.APPLY_RECORD_CONFIGURATION:
        return "Record Automation";
      case JobTypes.APPLY_CALCULATOR_CONFIGURATION:
        return "Estimation Method Configuration";
      case JobTypes.RUN_CALCULATION:
        return "Calculation Run";
      case JobTypes.APPLY_DEFAULT_CALCULATOR_CONFIGURATION_AND_RUN_CALCULATION:
        return "Configuration and Calculation Run";
      case JobTypes.APPLY_DEFAULT_RECORDS_BY_CONFIG_FILTER:
        return "Seeded Record Automation";
      case JobTypes.BULK_DELETE_FORM_SUBMISSIONS:
        return "Bulk Delete Form Submissions";
      case JobTypes.UPDATE_ASSET_STATUS:
        return "Update Asset Status";
      default:
        return "default";
    }
  };

  const onJobClick = (item: JobType) => {
    setSelectedJob(item);
  };

  const getJobItemIndicatorDetails = (job: JobType) => {
    if (job.status === JobStatus.FINISHED) {
      if (
        !isEmpty(job.item_status_counts) &&
        job.item_status_counts.success === job.total_items
      ) {
        return {
          content: "All items in this job were processed successfully",
          variant: "check-circle",
          colour: colours.colours.status.success,
        };
      } else if (job.item_status_counts.failed) {
        return {
          content: "Some items in this job have failed",
          variant: "x-circle",
          colour: colours.colours.status.error,
        };
      } else if (job.item_status_counts.skipped) {
        return {
          content: "Some items in this job were skipped",
          variant: "warning",
          colour: colours.colours.status.warning,
        };
      } else {
        return {
          content: "Job details unknown",
          variant: "info",
          colour: colours.colours.status.info,
        };
      }
    } else if (job.status === JobStatus.FAILED) {
      return {
        content: "This job has failed",
        variant: "x-circle",
        colour: colours.colours.status.error,
      };
    } else if (job.status === JobStatus.STARTED) {
      return {
        content: "This job is in progress",
        variant: "info",
        colour: colours.colours.status.info,
      };
    } else {
      return {
        content: "Job details unknown",
        variant: "info",
        colour: colours.colours.status.info,
      };
    }
  };

  const headers: Array<HeaderType<JobType>> = [
    {
      key: JobSchema.keyof().Enum.type,
      label: "Run Type",
      isSortable: true,
      renderComponent: ({ item }) => (
        <Link
          label={item.type ? getJobTypeDisplayLabel(item.type) : ""}
          onClick={() => {
            onJobClick(item);
          }}
        />
      ),
    },
    {
      key: JobSchema.keyof().Enum.status,
      label: "Status",
      isSortable: true,
      renderComponent: ({ item }) => (
        <Pill variant={getJobsStatusVariant(item.status)}>
          {getStatusDisplayLabel(item.status)}
        </Pill>
      ),
    },
    {
      key: JobSchema.keyof().Enum.total_items,
      label: "Total Items [#]",
      alignment: "right",
      renderComponent: ({ item }) => {
        const jobItemIndicatorDetails = getJobItemIndicatorDetails(item);
        return (
          <div className={cx("total-items-container")}>
            <DataTable.DataRow.NumberCell value={item.total_items} />
            <Tooltip
              content={jobItemIndicatorDetails?.content}
              contentClassName={cx("tooltip-container")}
            >
              <Icon
                variant={
                  jobItemIndicatorDetails?.variant as IconProps["variant"]
                }
                colour={jobItemIndicatorDetails?.colour}
                size={16}
              />
            </Tooltip>
          </div>
        );
      },
    },
    {
      key: JobSchema.keyof().Enum.duration,
      label: "Duration [Mins]",
      renderComponent: ({ item }) => (
        <DataTable.DataRow.TextCell
          value={`${Math.floor(item.duration / 60000)} min ${Math.floor((item.duration % 60000) / 1000)} s`}
        />
      ),
    },
    {
      label: "Started By",
      key: "started_by",
      renderComponent: ({ item }) =>
        item.started_by ? (
          <UserDataDisplay
            id={item.started_by}
            name={runLogStartedByUsersMap[item.started_by]?.name ?? "n/a"}
          />
        ) : (
          "-"
        ),
    },
    {
      key: JobSchema.keyof().Enum.started_at,
      label: "Started At",
      isSortable: true,
      renderComponent: ({ item }) => (
        <DataTable.DataRow.DateCell
          value={item.started_at}
          withTime
        />
      ),
    },
  ];

  useEffect(() => {
    if (!defaultJobId || !data?.data) return;

    const defaultJobDetails = data.data.find((job) => job.id === defaultJobId);

    if (defaultJobDetails) {
      setSelectedJob(defaultJobDetails);
    }
  }, [data?.data, setSelectedJob, defaultJobId]);

  return (
    <>
      <DataTablePanel
        storageKey={tableConfigStorageKey}
        panelProps={{ title: "Run Log" }}
        dataTableProps={{
          items,
          getItemId: (item) => item.id,
          headers,
          isLoading: isLoading,
          onSortChange: updateTableState,
          onPaginationChange: updateTableState,
          sorting: {
            sortBy: tableState.sortBy,
            sortDirection: tableState.sortDirection,
          },
          pagination: {
            page: tableState.page,
            pageSize: tableState.pageSize,
            total: data?.total_entries,
          },
        }}
      />
      <JobDrawer
        setSelectedJob={setSelectedJob}
        selectedJob={selectedJob}
      />
    </>
  );
};
